import { useState, useEffect } from 'react';
import { MessageSquareText, X, Send, Loader2 } from 'lucide-react';
import { fetchWithAuth } from '@/utils/api';
import { toast } from 'sonner';
import { trackEvent } from '@/utils/analytics';

const STORAGE_KEY = 'to_feedback_prompt_dismissed';

/**
 * Feedback Prompt Modal.
 *
 * Shows a popup asking the user for product feedback after they've created
 * their 3rd minutes entry — a point where they've used the tool enough to
 * have opinions, but are still in the active onboarding phase.
 *
 * Contains a simple text field: "What's missing? How can it be easier or clearer?"
 * Submits to /api/feedback POST endpoint.
 * Shows once per user — dismissal is persisted in localStorage.
 *
 * Props:
 *   show — boolean, parent controls visibility based on minutes count
 */
export function FeedbackPromptModal({ show }) {
  const [visible, setVisible] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setVisible(true);
      trackEvent('feedback_prompt_shown', { source: 'third_minutes_created' });
    }
  }, [show]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    trackEvent('feedback_prompt_dismissed', { source: 'third_minutes_created' });
  };

  const submit = async () => {
    if (!feedback.trim()) {
      toast.error('Please enter your feedback before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchWithAuth('/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: feedback.trim() }),
      });
      if (!res.ok) throw new Error('Failed to submit feedback');
      localStorage.setItem(STORAGE_KEY, 'true');
      setVisible(false);
      setFeedback('');
      toast.success('Thank you! Your feedback helps us improve TrustOffice.');
      trackEvent('feedback_prompt_submitted', { source: 'third_minutes_created' });
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error('Could not submit feedback. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40" data-testid="feedback-prompt-modal">
      <div className="bg-white border border-navy/20 max-w-md w-full mx-4 p-6 relative" style={{ animation: 'zoom-in 0.2s ease-out' }}>
        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute right-4 top-4 text-muted-foreground hover:text-navy transition-colors"
          data-testid="feedback-prompt-close"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="w-10 h-10 bg-navy/5 flex items-center justify-center mb-4">
          <MessageSquareText className="w-5 h-5 text-navy" />
        </div>

        <h2 className="font-serif text-xl text-navy mb-2">
          How can we make TrustOffice better?
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          You've been using TrustOffice for a bit now. We'd love to hear what's missing,
          what could be easier, or what could be clearer.
        </p>

        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What's missing? How can it be easier? How can it be more clear?"
          className="w-full min-h-[100px] p-3 border border-navy/20 text-sm text-navy placeholder:text-muted-foreground/60 focus:outline-none focus:border-gold transition-colors resize-none"
          data-testid="feedback-prompt-input"
          maxLength={1000}
        />
        <p className="text-xs text-muted-foreground/60 mt-1 text-right">
          {feedback.length}/1000
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <button
            onClick={submit}
            disabled={submitting || !feedback.trim()}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="feedback-prompt-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4" /> Submit Feedback</>
            )}
          </button>
          <button
            onClick={dismiss}
            className="flex-1 btn-secondary"
            data-testid="feedback-prompt-later"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default FeedbackPromptModal;